"use client";

import TokenImage from "@/components/TokenImage";
import KolPriceWidget from "@/components/kolToken/KolPriceWidget";
import SwapWidget from "@/components/uniswap/SwapWidget";
import { Button } from "@headlessui/react";
import { useQueryState } from "nuqs";
import { Suspense, useEffect, useMemo, useState } from "react";

export type TokenInfo = {
  token_symbol: string;
  erc20_address: string | null;
  decimals: number;
  coingecko_symbol: string | null;
  pool_address: string | null;
  name: string | null;
  release_date: string | null;
  description: string | null;
  twitter_url: string | null;
};

// Ensure tokens array is never empty
export type NonEmptyArray<T> = [T, ...T[]];

// Helper function to check if a token symbol is upcoming
const getReleaseDate = (tokenSymbol: string, upcomingTokens: TokenInfo[]): string | null => {
  const upcomingToken = upcomingTokens.find((token) => token.token_symbol === tokenSymbol);
  return upcomingToken?.release_date || null;
};

// Helper function to format release date
const formatReleaseDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Client component that uses useSearchParams
function TokensPageContent({
  tokens,
  upcomingTokens,
}: {
  tokens: NonEmptyArray<TokenInfo>;
  upcomingTokens: TokenInfo[];
}) {
  const [tokenSymbol, setTokenSymbol] = useQueryState("symbol", {
    defaultValue: tokens[0].token_symbol.toLowerCase(),
  });

  // Combine live tokens with upcoming tokens for display
  const allTokensForDisplay = [...tokens, ...upcomingTokens];

  const selectedToken = useMemo(() => {
    const foundToken = tokens.find(
      (token) =>
        token.token_symbol.toLowerCase() === tokenSymbol.toLowerCase() ||
        token.name?.toLowerCase() === tokenSymbol.toLowerCase(),
    );
    return foundToken ?? tokens[0];
  }, [tokens, tokenSymbol]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Filter tokens based on search query
  const filteredTokens = useMemo(() => {
    return allTokensForDisplay.filter(
      (token) =>
        token.token_symbol.toLowerCase().includes(searchQuery) || token.name?.toLowerCase().includes(searchQuery),
    );
  }, [searchQuery, allTokensForDisplay.filter]);

  const tokensPerPage = 10;
  let defaultPage = 1;

  const tokenIndex = filteredTokens.findIndex((token) => token.token_symbol === selectedToken.token_symbol);
  if (tokenIndex !== -1) {
    defaultPage = Math.floor(tokenIndex / tokensPerPage) + 1;
  }

  const [currentPage, setCurrentPage] = useState<number>(defaultPage);

  // Calculate total pages
  const totalPages = Math.ceil(filteredTokens.length / tokensPerPage);

  // Get current tokens to display
  const displayedTokens = useMemo(() => {
    const startIndex = (currentPage - 1) * tokensPerPage;
    return filteredTokens.slice(startIndex, startIndex + tokensPerPage);
  }, [filteredTokens, currentPage]);

  // Reset to page 1 when search query changes
  //biome-ignore lint/correctness/useExhaustiveDependencies: we want reset pagination on searchQuery updates
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  return (
    <>
      {/* Hero section */}
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold mb-4  text-white">KOL Tokens Marketplace</h1>
        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
          Discover and trade tokens from your favorite content creators. Each token represents a unique opportunity to
          support and engage with creators.
        </p>
      </div>

      {/* Token selector */}
      {tokens.length > 0 && (
        <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-6 border border-gray-800/50 shadow-lg mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h2 className="text-xl font-semibold text-gray-300">Select a Token</h2>
            {tokens.length > 9 && (
              <div className="relative w-full md:w-64">
                <input
                  type="text"
                  placeholder="Search tokens..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <title>decoration</title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Token grid with pagination */}
          <div className="grid grid-cols-2 text-gray-300 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {displayedTokens.map((token) => {
              const releaseDate = getReleaseDate(token.token_symbol, upcomingTokens);
              const isUpcoming = !!releaseDate;

              return (
                <div key={token.token_symbol} className="relative">
                  <Button
                    onClick={() => {
                      if (!isUpcoming) {
                        setTokenSymbol(token.token_symbol.toLowerCase());
                      }
                    }}
                    disabled={isUpcoming}
                    className={`flex items-center p-3 rounded-lg transition-all w-full ${
                      selectedToken?.token_symbol === token.token_symbol
                        ? "bg-blue-900/50 border-2 border-blue-500"
                        : isUpcoming
                          ? "bg-gray-800/30 border border-gray-600 cursor-not-allowed opacity-50 grayscale"
                          : "bg-gray-800/50 border border-gray-700 hover:bg-gray-700/50"
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full overflow-hidden mr-3 flex-shrink-0 border border-gray-700">
                      <TokenImage symbol={token.token_symbol} size={40} className="object-cover w-full h-full" />
                    </div>
                    <div className="text-left overflow-hidden">
                      <div className="font-bold truncate">{token.token_symbol}</div>
                      <div className="text-xs text-white-400 truncate">{token.name}</div>
                    </div>
                  </Button>

                  {/* Upcoming token overlay */}
                  {isUpcoming && releaseDate && (
                    <div className="absolute top-1 right-1 bg-black/60 rounded px-1.5 py-0.5">
                      <div className="text-xs text-white whitespace-nowrap">{formatReleaseDate(releaseDate)}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination controls */}
          {filteredTokens.length > tokensPerPage && (
            <div className="flex justify-center mt-6">
              <div className="flex space-x-2">
                <Button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-md bg-gray-800 text-white disabled:opacity-50"
                >
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 rounded-md ${
                      currentPage === page ? "bg-blue-600 text-white" : "bg-gray-800 text-white hover:bg-gray-700"
                    }`}
                  >
                    {page}
                  </Button>
                ))}
                <Button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-md bg-gray-800 text-white disabled:opacity-50"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* KOL Profile Section */}
      {selectedToken && (
        <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-4 border border-gray-800/50 shadow-lg mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-start">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {selectedToken.twitter_url ? (
                <a
                  href={selectedToken.twitter_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:opacity-80 transition-opacity"
                >
                  <img
                    src={`/token_logos/${selectedToken.token_symbol.toLowerCase()}_avatar.png`}
                    alt={`${selectedToken.name || selectedToken.token_symbol} avatar`}
                    className="w-16 h-16 rounded-full border-2 border-gray-700 object-cover"
                    onError={(e) => {
                      // Fallback to token image if avatar doesn't exist
                      const target = e.target as HTMLImageElement;
                      target.src = `/token_logos/${selectedToken.token_symbol.toLowerCase()}.png`;
                    }}
                  />
                </a>
              ) : (
                <img
                  src={`/token_logos/${selectedToken.token_symbol.toLowerCase()}_avatar.png`}
                  alt={`${selectedToken.name || selectedToken.token_symbol.toLowerCase()} avatar`}
                  className="w-16 h-16 rounded-full border-2 border-gray-700 object-cover"
                  onError={(e) => {
                    // Fallback to token image if avatar doesn't exist
                    const target = e.target as HTMLImageElement;
                    target.src = `/token_logos/${selectedToken.token_symbol.toLowerCase()}.png`;
                  }}
                />
              )}
            </div>

            {/* Creator Info */}
            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
                <h3 className="text-lg font-semibold text-white">{selectedToken.name || selectedToken.token_symbol}</h3>
                {selectedToken.twitter_url && (
                  <a
                    href={selectedToken.twitter_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <title>twitter</title>
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                    Follow on Twitter
                  </a>
                )}
              </div>

              {selectedToken.description && (
                <div className="text-gray-300 leading-relaxed text-sm whitespace-pre-line">
                  {selectedToken.description}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Token market panel */}
      {selectedToken?.erc20_address && selectedToken?.pool_address && (
        <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-gray-800/50 shadow-lg mb-6 overflow-hidden">
          <h2 className="text-xl font-semibold text-white mb-4 px-2">
            {selectedToken.name || selectedToken.token_symbol} Token Market
          </h2>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Price chart widget */}
            <div className="w-full md:w-[65%]">
              <div className="bg-gray-800/50 text-gray-300 rounded-xl p-4 h-full">
                <KolPriceWidget
                  tokenSymbol={selectedToken.token_symbol}
                  tokenAddress={selectedToken.erc20_address}
                  poolAddress={selectedToken.pool_address} // Mock pool address
                />
              </div>
            </div>

            {/* Uniswap Swap widget */}
            <div className="w-full md:w-[35%]">
              <SwapWidget
                tokenInfo={{
                  symbol: selectedToken.token_symbol,
                  erc20Address: selectedToken.erc20_address,
                  decimals: selectedToken.decimals,
                  coingeckoSymbol: selectedToken.coingecko_symbol ?? "",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Call to action for token creation */}
      <div className="text-center mt-12">
        <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 shadow-lg">
          <h3 className="text-2xl font-bold text-white mb-4">Ready to Launch Your Token?</h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Join the KOL ecosystem and create your own token to connect with your audience and unlock new monetization
            opportunities.
          </p>
          <a
            href="mailto:contact@kolz.chat"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
          >
            Create Your Own Token
          </a>
        </div>
      </div>
    </>
  );
}

// Main page component with Suspense boundary
export default function Tokens({
  tokens,
  upcomingTokens = [],
}: {
  tokens: NonEmptyArray<TokenInfo>;
  upcomingTokens?: TokenInfo[];
}) {
  return (
    <section id="pricing" className="flex items-center flex-col mt-24 text-white pb-32 w-full">
      <div className="w-full max-w-7xl mx-auto px-6">
        <Suspense
          fallback={
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
            </div>
          }
        >
          <TokensPageContent tokens={tokens} upcomingTokens={upcomingTokens} />
        </Suspense>
      </div>
    </section>
  );
}
